import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Award, Download, ExternalLink, ShieldCheck, ShieldX,
  Loader2, BookOpen, Calendar, Building2, GraduationCap,
} from 'lucide-react';
import { toast } from 'sonner';
import { generateCertificatePDF } from '@/lib/certificatePdf';

const MyCertificatesPage = () => {
  const { api } = useAuth();
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => { fetchCerts(); }, []);

  const fetchCerts = async () => {
    try {
      const res = await api.get('/certificates');
      setCertificates(res.data || []);
    } catch {
      toast.error('Failed to load certificates');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (cert) => {
    setDownloading(cert.id);
    try {
      await generateCertificatePDF(cert);
      toast.success('Certificate downloaded!');
    } catch (e) {
      console.error(e);
      toast.error('Failed to generate PDF');
    } finally {
      setDownloading(null);
    }
  };

  const validCerts = certificates.filter(c => c.is_valid);
  const revokedCerts = certificates.filter(c => !c.is_valid);

  return (
    <DashboardLayout requiredRoles={['learner', 'org_admin', 'super_admin', 'content_manager']}>
      <div className="space-y-6" data-testid="my-certificates-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-[#2a3e5d]">My Certificates</h1>
            <p className="text-slate-500 mt-1">Your earned training certificates</p>
          </div>
          <Badge className="bg-[#02d1d4] text-[#2a3e5d] text-sm px-3 py-1">
            {validCerts.length} Certificate{validCerts.length !== 1 ? 's' : ''}
          </Badge>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-[#20acf6]" />
          </div>
        ) : certificates.length === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="py-16 text-center">
              <Award className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-700 mb-2">No Certificates Yet</h3>
              <p className="text-slate-500 mb-6">Complete a course to earn your first certificate.</p>
              <Button asChild className="bg-[#20acf6] hover:bg-[#3bb8f7]">
                <Link to="/courses">Browse Courses</Link>
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Valid Certificates */}
            <div className="grid gap-4">
              {validCerts.map(cert => (
                <CertificateCard
                  key={cert.id}
                  cert={cert}
                  onDownload={() => handleDownload(cert)}
                  downloading={downloading === cert.id}
                />
              ))}
            </div>

            {/* Revoked Certificates */}
            {revokedCerts.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                  <ShieldX className="h-4 w-4 text-red-400" /> Revoked Certificates
                </h3>
                <div className="grid gap-3 opacity-70">
                  {revokedCerts.map(cert => (
                    <CertificateCard key={cert.id} cert={cert} revoked />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

const CertificateCard = ({ cert, onDownload, downloading, revoked }) => {
  const date = new Date(cert.completion_date).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'long', year: 'numeric'
  });

  return (
    <Card className={`border-slate-200 hover:shadow-md transition-shadow ${revoked ? 'border-red-200' : ''}`} data-testid={`cert-card-${cert.id}`}>
      <CardContent className="p-5 flex items-start gap-4">
        <div className={`w-14 h-14 rounded-xl flex items-center justify-center shrink-0 ${revoked ? 'bg-red-100' : 'bg-gradient-to-br from-[#20acf6]/20 to-[#02d1d4]/20'}`}>
          {revoked ? <ShieldX className="h-7 w-7 text-red-500" /> : <Award className="h-7 w-7 text-[#2a3e5d]" />}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-semibold text-slate-900 line-clamp-1" data-testid={`cert-title-${cert.id}`}>
              {cert.course_title}
            </h3>
            {revoked && <Badge className="bg-red-100 text-red-700 text-xs shrink-0">Revoked</Badge>}
            {!revoked && cert.is_accredited && (
              <Badge className="bg-[#02d1d4] text-[#2a3e5d] text-xs shrink-0">
                <GraduationCap className="h-3 w-3 mr-1" /> Accredited
              </Badge>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-slate-500">
            <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> {date}</span>
            <span className="flex items-center gap-1 font-mono"><ShieldCheck className="h-3.5 w-3.5" /> {cert.certificate_number}</span>
            {cert.organisation_name && (
              <span className="flex items-center gap-1"><Building2 className="h-3.5 w-3.5" /> {cert.organisation_name}</span>
            )}
          </div>

          {revoked && cert.revoke_reason && (
            <p className="text-xs text-red-500 mt-1">Reason: {cert.revoke_reason}</p>
          )}
        </div>

        {!revoked && (
          <div className="flex items-center gap-2 shrink-0">
            <Button
              size="sm"
              variant="outline"
              className="text-xs"
              asChild
              data-testid={`verify-link-${cert.id}`}
            >
              <Link to={`/verify/${cert.certificate_number}`} target="_blank">
                <ExternalLink className="h-3.5 w-3.5 mr-1" /> Verify
              </Link>
            </Button>
            <Button
              size="sm"
              className="bg-[#2a3e5d] hover:bg-[#2a4f7a] text-xs"
              onClick={onDownload}
              disabled={downloading}
              data-testid={`download-cert-${cert.id}`}
            >
              {downloading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Download className="h-3.5 w-3.5 mr-1" />}
              PDF
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MyCertificatesPage;
